import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { get, set } from 'local-storage'
import { groupBy } from 'lodash'
import { useRouter } from 'next/router'

import { getUserName } from '../utils'

import { LocaleProvider } from '@/context/localeContext'
import { UsersProjects, useUser } from '@/context/userContext'
import { isProjectSelected as isProject } from '@/utils/isProjectSelected'

import { ProjectStatus } from 'types/api.graphql'
import { ProjectLike } from 'types/vagueTypes'

const ALL_PROJECTS = 'All projects'

type CompanyProject = {
  id: typeof BUILDER_KEY
  name: string
  status: typeof BUILDER_PROJECT_STATUS
  address: null
  latitude: null
  longitude: null
  procoreId: null
  integrationConnections: null
  timezone: null
  country: null
  locale: null
  startDate: null
  endDate: null
  projectSettings: null
  siteTimings: null
  builder: {
    defaultLocale: null
    defaultTimezone: null
  }
}

export type StoredProject = UsersProjects[number] | CompanyProject

type ProjectContextValue = {
  project: StoredProject | null
  projects: Array<StoredProject>
  isProjectSelected: boolean
  projectsLoading: boolean
  hasProjects: boolean
  refetchProjects: ReturnType<typeof useUser>['refetchProjects']
  updateProject: (project: StoredProject) => void
  changeProjectId: (id: string) => void
  selectAllProjects: () => void
}

const LS_PROJECT_KEY = 'buildpass-project'

export const BUILDER_PROJECT_STATUS = 'ALL' as const
export const BUILDER_KEY = 'builder' as const

export const ProjectContext = createContext<ProjectContextValue>(null)

export const ProjectProvider = ({ children }) => {
  const storedProject = get<StoredProject>(LS_PROJECT_KEY)
  const router = useRouter()

  const {
    projects: usersProjects,
    projectsLoading,
    builder,
    user,
    refetchProjects,
  } = useUser()

  const [project, setProject] = useState(storedProject)

  const localeCountryCode = useMemo(() => {
    if (project?.locale) {
      return project.locale
    }

    return builder?.defaultLocale
  }, [project, builder])

  const updateProject = useCallback(
    (project: StoredProject) => {
      set(LS_PROJECT_KEY, project)
      setProject(project)
    },
    [setProject]
  )

  const changeProjectId = useCallback(
    (id: string) => {
      const project = usersProjects.find((p) => p.id === id)
      updateProject(project)
    },
    [updateProject, usersProjects]
  )

  const projects = useMemo(() => {
    const companyProject = {
      id: BUILDER_KEY,
      name: ALL_PROJECTS,
      status: BUILDER_PROJECT_STATUS,
    } as CompanyProject

    return [companyProject, ...(usersProjects ?? [])]
  }, [usersProjects])

  const isProjectSelected = useMemo(() => isProject(project), [project])

  const hasProjects = useMemo(() => {
    return projects?.some((p) => p.id !== BUILDER_KEY) ?? false
  }, [projects])

  /**
   * If a project is selected but the project isn't in the list of projects the user has access to,
   * clear the project and set it to null.
   */
  useEffect(() => {
    if (!project?.id) return // if there's no project selected, do nothing
    if (!isProjectSelected) return // if the project isn't selected, do nothing
    if (!usersProjects?.length) return // if there are no projects, do nothing
    if (projectsLoading) return // if projects are loading, do nothing

    if (!usersProjects.some((p) => p.id === project.id)) {
      updateProject(null)
    }
  }, [
    isProjectSelected,
    projectsLoading,
    project?.id,
    usersProjects,
    updateProject,
  ])

  /**
   * If a `projectId` has been set in the query param then we clear
   * the current project and hold onto the ID until we've fetched all
   * the projects. Then we set the current project with the matching id
   */
  const forcedProjectId = useRef<string>(undefined)
  const processedQueryParams = useRef(false)
  const queryProjectId = router.query?.projectId

  useEffect(() => {
    if (processedQueryParams.current) return

    if (!!queryProjectId && queryProjectId !== project?.id) {
      processedQueryParams.current = true
      updateProject(null)
      forcedProjectId.current = queryProjectId.toString()
    }
  }, [project?.id, queryProjectId, updateProject])

  /**
   * If we've got a forced project ID then find it and set it. Otherwise,
   * if we've got a current project, find the latest copy of it and set it
   * incase it's been updated
   */
  const processedForceProjectId = useRef(false)

  useEffect(() => {
    if (processedForceProjectId.current) return

    // if forcing a project, update it
    if (projects?.length && forcedProjectId.current) {
      processedForceProjectId.current = true
      const selectedProject = projects.find(
        (p) => p.id === forcedProjectId.current
      )

      if (!selectedProject && projectsLoading) return // if we're still loading projects, do nothing

      if (selectedProject) {
        forcedProjectId.current = null
        updateProject(selectedProject)
        return
      }
    }

    // update the selected project with its most up-to-date version of itself
    if (projects?.length && isProject(project)) {
      processedForceProjectId.current = true
      const refreshedProject = projects.find((p) => p.id === project.id)

      if (!refreshedProject && projectsLoading) return // if we're still loading projects, do nothing

      updateProject(refreshedProject ?? null)
    }
  }, [projects, project, projectsLoading, updateProject])

  const selectAllProjects = useCallback(() => {
    const builderProject = projects.find((p) => p.id === BUILDER_KEY)
    if (builderProject) {
      updateProject(builderProject)
    }
  }, [projects, updateProject])

  return (
    <ProjectContext.Provider
      value={{
        project,
        projects,
        isProjectSelected,
        projectsLoading,
        hasProjects,
        updateProject,
        changeProjectId,
        selectAllProjects,
        refetchProjects,
      }}
    >
      <LocaleProvider
        localeCountryCode={localeCountryCode}
        buildPassOptions={{
          projectName: project?.name,
          builderName: builder?.company?.name,
          userName: getUserName(user),
        }}
      >
        {children}
      </LocaleProvider>
    </ProjectContext.Provider>
  )
}

export const useProject = () => useContext(ProjectContext)

type GroupedProjects<T> = Record<ProjectStatus, T[] | StoredProject[]>

export const getActiveAndGroupedProjects = <T extends ProjectLike>(
  projects: T[],
  includeBuilder: boolean
): [GroupedProjects<T>, T[]] => {
  const activeProjects = projects.filter((p) => p.id !== BUILDER_KEY)

  const sortedProjects = activeProjects.sort((p1, p2) => {
    if (p1.name > p2.name) return 1
    if (p1.name < p2.name) return -1
    return 0
  })

  const groupedProjects = groupBy(
    sortedProjects,
    (proj) => proj.status
  ) as GroupedProjects<T>

  if (includeBuilder) {
    groupedProjects[BUILDER_PROJECT_STATUS] = [
      {
        id: BUILDER_KEY,
        name: ALL_PROJECTS,
        status: BUILDER_PROJECT_STATUS,
      } as T,
    ]
  }

  return [groupedProjects, activeProjects]
}

export const useGroupedProjects = (
  includeBuilder = true
): [GroupedProjects<StoredProject>, StoredProject[]] => {
  const { projects } = useProject()

  const [groupedProjects, activeProjects] = useMemo(() => {
    return getActiveAndGroupedProjects(projects, includeBuilder)
  }, [projects, includeBuilder])

  return [groupedProjects, activeProjects as StoredProject[]]
}
